/**
 * reportDetailTransformer.js
 *
 * Transforms the new Report Detail API response into shapes expected by the updated ReportDetail components.
 * Follows docs/fe-report-detail-integration.md schema.
 */

/**
 * Format a number to compact representation (e.g. 1.2M, 5.4K)
 * @param {number} val
 * @param {number} decimals
 * @returns {string|number|null}
 */
export const formatCompactNumber = (val, decimals = 1) => {
  if (val == null || isNaN(val)) return null;
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: decimals,
  }).format(val);
};

/**
 * Transforms the raw API response from POST /explore/benchmarking/institution/report_detail.
 * 
 * New response fields:
 * - entity: EntityCard
 * - kpis: InstitutionKPIs (publications, citations, researchers, h_index)
 * - research_strength: ResearchStrength (top_topics, top_active_topics)
 * - top_ranking: OverviewTopicItem[]
 * - top_faculty: TopFacultyOverview (by_h_index, by_grants)
 * - top_sponsors: TopSponsorsOverview (items, unique_sponsors)
 * - featured_grants: FeaturedGrantItem[]
 * - hidden_fields: Record<string, boolean>
 * - errors: Record<string, string>
 * - time_taken: number
 *
 * @param {import("Explore/Config/reportDetailConfig").ReportDetailResponse|null} reportResponse
 * @returns {Object|null}
 */
export const transformReportDetailResponse = (reportResponse) => {
  if (!reportResponse) return null;

  const {
    entity = null,
    kpis = null,
    research_strength = null,
    top_ranking = [],
    top_faculty = null,
    top_sponsors = null,
    featured_grants = [],
    hidden_fields = null,
    errors = {},
    time_taken = 0,
  } = reportResponse;

  return {
    entity,
    kpis,
    // Provide both snake_case and camelCase accessors for template flexibility
    research_strength,
    researchStrength: research_strength,
    top_ranking: top_ranking || [],
    topRanking: top_ranking || [],
    top_faculty,
    topFaculty: top_faculty,
    top_sponsors,
    topSponsors: top_sponsors,
    featured_grants: featured_grants || [],
    featuredGrants: featured_grants || [],
    hidden_fields: hidden_fields || {},
    hiddenFields: hidden_fields || {},
    errors: errors || {},
    time_taken,
    timeTaken: time_taken,
  };
};
